import React from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Link,
  Table,
  Card,
  Icon,
  HStack,
  Button,
} from '@chakra-ui/react';
import { Thead, Tbody, Tr, Th, Td, TableContainer } from '@/components/ui/chakra-v2-compat';
import { FaArrowLeft, FaCookieBite } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO.jsx';

const CookiePolicy = () => {
  const navigate = useNavigate();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box bg={bg} minH="100vh" py={8}>
      <SEO
        title="Política de Cookies"
        description="Información sobre las cookies utilizadas en Cadencya y cómo gestionarlas."
        path="/politica-cookies"
      />
      <Container maxW="4xl">
        <VStack gap={8} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="center">
            <Button variant="ghost" onClick={() => navigate(-1)}><Icon asChild><FaArrowLeft /></Icon>Volver
                          </Button>
          </HStack>

          <Card.Root bg={cardBg} shadow="lg" rounded="2xl">
            <Card.Body p={{ base: 6, md: 10 }}>
              <VStack gap={8} align="stretch">
                {/* Título */}
                <HStack gap={4}>
                  <Icon w={10} h={10} color="orange.400" asChild><FaCookieBite /></Icon>
                  <Heading size="xl">Política de Cookies</Heading>
                </HStack>

                <Text fontSize="sm" color="gray.500">
                  Última actualización: 25 de febrero de 2026
                </Text>

                {/* Introducción */}
                <VStack align="stretch" gap={4}>
                  <Heading size="md">¿Qué son las cookies?</Heading>
                  <Text>
                    Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo 
                    (ordenador, tablet o móvil) cuando visitas un sitio web. Se utilizan ampliamente 
                    para hacer que los sitios web funcionen de manera más eficiente, así como para 
                    proporcionar información a los propietarios del sitio.
                  </Text>
                </VStack>

                {/* Tipos de cookies */}
                <VStack align="stretch" gap={4}>
                  <Heading size="md">Tipos de cookies que utilizamos</Heading>
                  
                  <Table.ScrollArea>
                    <Table.Root variant="simple" size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                          <Table.ColumnHeader>Descripción</Table.ColumnHeader>
                          <Table.ColumnHeader>Duración</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell fontWeight="bold">Esenciales</Table.Cell>
                          <Table.Cell>
                            Necesarias para el funcionamiento básico del sitio. Incluyen cookies 
                            de autenticación, seguridad y preferencias del usuario.
                          </Table.Cell>
                          <Table.Cell>Sesión / 7 días</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell fontWeight="bold">Análisis</Table.Cell>
                          <Table.Cell>
                            Nos ayudan a entender cómo los visitantes interactúan con el sitio, 
                            recopilando información de forma anónima.
                          </Table.Cell>
                          <Table.Cell>Hasta 2 años</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell fontWeight="bold">Marketing</Table.Cell>
                          <Table.Cell>
                            Se utilizan para rastrear visitantes en sitios web con el objetivo de 
                            mostrar anuncios relevantes.
                          </Table.Cell>
                          <Table.Cell>Hasta 2 años</Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </VStack>

                {/* Cookies específicas */}
                <VStack align="stretch" gap={4}>
                  <Heading size="md">Cookies específicas que utilizamos</Heading>
                  
                  <Table.ScrollArea>
                    <Table.Root variant="simple" size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                          <Table.ColumnHeader>Proveedor</Table.ColumnHeader>
                          <Table.ColumnHeader>Propósito</Table.ColumnHeader>
                          <Table.ColumnHeader>Tipo</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        <Table.Row>
                          <Table.Cell>cadencya_cookie_consent</Table.Cell>
                          <Table.Cell>Cadencya</Table.Cell>
                          <Table.Cell>Almacena tus preferencias de cookies</Table.Cell>
                          <Table.Cell>Esencial</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>cadencya_token</Table.Cell>
                          <Table.Cell>Cadencya</Table.Cell>
                          <Table.Cell>Mantiene tu sesión iniciada</Table.Cell>
                          <Table.Cell>Esencial</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>cadencya_refresh_token</Table.Cell>
                          <Table.Cell>Cadencya</Table.Cell>
                          <Table.Cell>Renueva tu sesión de forma segura</Table.Cell>
                          <Table.Cell>Esencial</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>_ga, _gid</Table.Cell>
                          <Table.Cell>Google Analytics</Table.Cell>
                          <Table.Cell>Análisis de uso del sitio</Table.Cell>
                          <Table.Cell>Análisis</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell>sentry-*</Table.Cell>
                          <Table.Cell>GlitchTip (servidor propio)</Table.Cell>
                          <Table.Cell>Monitorización de errores técnicos para mejorar la estabilidad de la plataforma</Table.Cell>
                          <Table.Cell>Análisis</Table.Cell>
                        </Table.Row>
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </VStack>

                {/* Control de cookies */}
                <VStack align="stretch" gap={4}>
                  <Heading size="md">¿Cómo controlar las cookies?</Heading>
                  <Text>
                    Puedes configurar tus preferencias de cookies en cualquier momento a través 
                    del banner que aparece al visitar nuestro sitio. También puedes gestionar 
                    las cookies desde la configuración de tu navegador:
                  </Text>
                  <VStack align="start" gap={2} pl={4}>
                    <Link
                      href="https://support.google.com/chrome/answer/95647"
                      color="purple.500"
                      target='_blank'
                      rel='noopener noreferrer'>
                      • Google Chrome
                    </Link>
                    <Link
                      href="https://support.mozilla.org/es/kb/cookies-informacion-que-los-sitios-web-guardan-en-"
                      color="purple.500"
                      target='_blank'
                      rel='noopener noreferrer'>
                      • Mozilla Firefox
                    </Link>
                    <Link
                      href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                      color="purple.500"
                      target='_blank'
                      rel='noopener noreferrer'>
                      • Safari
                    </Link>
                    <Link
                      href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                      color="purple.500"
                      target='_blank'
                      rel='noopener noreferrer'>
                      • Microsoft Edge
                    </Link>
                  </VStack>
                </VStack>

                {/* Tus derechos */}
                <VStack align="stretch" gap={4}>
                  <Heading size="md">Tus derechos</Heading>
                  <Text>
                    Conforme al Reglamento General de Protección de Datos (RGPD), tienes derecho a:
                  </Text>
                  <VStack align="start" gap={1} pl={4}>
                    <Text>• Acceder a tus datos personales</Text>
                    <Text>• Rectificar datos inexactos</Text>
                    <Text>• Solicitar la eliminación de tus datos</Text>
                    <Text>• Oponerte al procesamiento de tus datos</Text>
                    <Text>• Solicitar la portabilidad de tus datos</Text>
                  </VStack>
                </VStack>

                {/* Contacto */}
                <VStack align="stretch" gap={4}>
                  <Heading size="md">Contacto</Heading>
                  <Text>
                    Si tienes alguna pregunta sobre nuestra política de cookies, puedes contactarnos en:{' '}
                    <Link href="mailto:info@cadencya.com" color="purple.500">
                      info@cadencya.com
                    </Link>
                  </Text>
                </VStack>

                {/* Botón para volver a configurar */}
                <Box pt={4}>
                  <Button
                    colorPalette="purple"
                    onClick={() => {
                      localStorage.removeItem('cadencya_cookie_consent');
                      window.location.reload();
                    }}
                  >
                    Modificar preferencias de cookies
                  </Button>
                </Box>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    </Box>
  );
};

export default CookiePolicy;

